import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Divider,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Search as SearchIcon,
  Security as SecurityIcon,
  Schedule as ScheduleIcon,
  Star as StarIcon,
  Support as SupportIcon,
  Payment as PaymentIcon,
  Verified as VerifiedIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  Work as WorkIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

export const MembershipBenefitsSection: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const patientBenefits = [
    {
      icon: <SearchIcon />,
      title: 'Find Qualified Caregivers',
      description: 'Search and filter through verified, licensed healthcare professionals in your area.',
    },
    {
      icon: <VerifiedIcon />,
      title: 'Background Checked Professionals',
      description: 'All caregivers undergo thorough background checks and credential verification.',
    },
    {
      icon: <StarIcon />,
      title: 'Verified Reviews & Ratings',
      description: 'Read authentic reviews from other patients to make informed decisions.',
    },
    {
      icon: <SecurityIcon />,
      title: 'Secure Booking & Payment',
      description: 'Safe, encrypted payment processing with insurance and liability coverage.',
    },
    {
      icon: <ScheduleIcon />,
      title: 'Flexible Scheduling',
      description: 'Book appointments that fit your schedule with real-time availability.',
    },
    {
      icon: <SupportIcon />,
      title: '24/7 Customer Support',
      description: 'Get help whenever you need it with our dedicated support team.',
    },
  ];

  const caregiverBenefits = [
    {
      icon: <WorkIcon />,
      title: 'Flexible Work Schedule',
      description: 'Set your own hours and work when it suits your lifestyle.',
    },
    {
      icon: <TrendingUpIcon />,
      title: 'Competitive Rates',
      description: 'Earn what you deserve with transparent, competitive pricing.',
    },
    {
      icon: <PeopleIcon />,
      title: 'Quality Patient Matches',
      description: 'Connect with patients who need your specific expertise and skills.',
    },
    {
      icon: <PaymentIcon />,
      title: 'Secure Payments',
      description: 'Get paid reliably and on time with our secure payment system.',
    },
    {
      icon: <AssessmentIcon />,
      title: 'Professional Tools',
      description: 'Access scheduling, documentation, and communication tools.',
    },
    {
      icon: <SupportIcon />,
      title: 'Business Support',
      description: 'Get help growing your practice with marketing and administrative support.',
    },
  ];

  const handleGetStarted = () => {
    navigate('/auth');
  };

  const handleBecomeCaregiver = () => {
    navigate('/auth?role=caregiver');
  };

  return (
    <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: theme.palette.grey[50] }}>
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 'bold',
              color: theme.palette.text.primary,
              mb: 2,
            }}
          >
            Why Choose Careva?
          </Typography>
          <Typography
            variant="h6"
            component="p"
            sx={{
              color: theme.palette.text.secondary,
              maxWidth: '600px',
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            Whether you're looking for care or providing it, Careva offers 
            comprehensive benefits designed to meet your unique needs.
          </Typography>
        </Box>

        <Grid container spacing={6}>
          {/* Patient Benefits */}
          <Grid item xs={12} lg={6}>
            <Card
              elevation={2}
              sx={{
                height: '100%',
                border: `2px solid ${theme.palette.primary.light}`,
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.primary.light,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    <PeopleIcon sx={{ fontSize: '2rem', color: 'white' }} />
                  </Box>
                  <Typography
                    variant="h4"
                    component="h3"
                    sx={{
                      fontWeight: 'bold',
                      color: theme.palette.primary.main,
                      mb: 1,
                    }}
                  >
                    For Patients
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.text.secondary,
                    }}
                  >
                    Find the right caregiver for your healthcare needs
                  </Typography>
                </Box>

                <List sx={{ mb: 4 }}>
                  {patientBenefits.map((benefit, index) => (
                    <ListItem key={index} sx={{ px: 0, py: 1 }}>
                      <ListItemIcon
                        sx={{
                          minWidth: 40,
                          color: theme.palette.primary.main,
                        }}
                      >
                        {benefit.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 'bold',
                              color: theme.palette.text.primary,
                              mb: 0.5,
                            }}
                          >
                            {benefit.title}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.secondary,
                              lineHeight: 1.5,
                            }}
                          >
                            {benefit.description}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>

                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: theme.palette.text.secondary,
                      mb: 3,
                    }}
                  >
                    Ready to find your caregiver?
                  </Typography>
                  <Box
                    component="button"
                    onClick={handleGetStarted}
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      color: 'white',
                      border: 'none',
                      borderRadius: 2,
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      },
                    }}
                  >
                    Get Started Free
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Caregiver Benefits */}
          <Grid item xs={12} lg={6}>
            <Card
              elevation={2}
              sx={{
                height: '100%',
                border: `2px solid ${theme.palette.secondary.light}`,
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.secondary.light,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    <WorkIcon sx={{ fontSize: '2rem', color: 'white' }} />
                  </Box>
                  <Typography
                    variant="h4"
                    component="h3"
                    sx={{
                      fontWeight: 'bold',
                      color: theme.palette.secondary.main,
                      mb: 1,
                    }}
                  >
                    For Caregivers
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.text.secondary,
                    }}
                  >
                    Grow your practice and help more patients
                  </Typography>
                </Box>

                <List sx={{ mb: 4 }}>
                  {caregiverBenefits.map((benefit, index) => (
                    <ListItem key={index} sx={{ px: 0, py: 1 }}>
                      <ListItemIcon
                        sx={{
                          minWidth: 40,
                          color: theme.palette.secondary.main,
                        }}
                      >
                        {benefit.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 'bold',
                              color: theme.palette.text.primary,
                              mb: 0.5,
                            }}
                          >
                            {benefit.title}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.secondary,
                              lineHeight: 1.5,
                            }}
                          >
                            {benefit.description}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>

                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: theme.palette.text.secondary,
                      mb: 3,
                    }}
                  >
                    Ready to start your practice?
                  </Typography>
                  <Box
                    component="button"
                    onClick={handleBecomeCaregiver}
                    sx={{
                      backgroundColor: theme.palette.secondary.main,
                      color: 'white',
                      border: 'none',
                      borderRadius: 2,
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: theme.palette.secondary.dark,
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      },
                    }}
                  >
                    Become a Caregiver
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Bottom Section */}
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <Divider sx={{ mb: 4 }} />
          <Typography
            variant="h5"
            sx={{
              fontWeight: 'bold',
              color: theme.palette.text.primary,
              mb: 2,
            }}
          >
            Join the Careva Community Today
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.secondary,
              maxWidth: '600px',
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            Whether you're seeking care or providing it, Careva connects you with 
            the right people to make healthcare more accessible, convenient, and effective.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};
